import { useEffect, useState } from "react";
import { TableLoaderSmall } from "../../../components/LoaderC";
import styles from "./TimeTracking.module.css";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { useAuth0 } from "@auth0/auth0-react";

dayjs.extend(customParseFormat);

const timeToDecimal = (time) => {
    const parsed = dayjs(time, ['HH:mm:ss', 'h:mm A', 'H:mm']);

    const hours = parsed.hour();
    const minutes = parsed.minute();

    return hours + minutes / 60;
}

const formatMinutesToHM = (totalMinutes) => {
    if (totalMinutes === 0)
        return `-`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;

    return `${hours}h ${minutes}m`;
}

const toP = (h, base = 8, span = 14) => Math.max(0, Math.min(100, ((h - base) / span) * 100));

const DayModal = ({ setIsModalOpen, selectedEmp, selectedDay, setLoading, updateTimeTracking }) => {
    const { getAccessTokenSilently } = useAuth0();

    const [shiftOpen, setShiftOpen] = useState(false);
    const [shiftStart, setShiftStart] = useState(dayjs(selectedDay?.checkin_time, 'HH:mm:ss').format('HH:mm') || "09:00");
    const [shiftEnd, setShiftEnd] = useState(dayjs(selectedDay?.checkout_time, 'HH:mm:ss').format('HH:mm') || "17:00");
    const [saved, setSaved] = useState(false);

    const schedL = toP(timeToDecimal(selectedEmp?.checkin_time));
    const schedW = toP(timeToDecimal(selectedEmp?.checkout_time)) - schedL;
    const ciL = toP(timeToDecimal(selectedDay?.checkin_time) || timeToDecimal(selectedEmp?.checkin_time));
    const workedW = toP(timeToDecimal(selectedDay?.checkout_time) || dayjs().format('hh:mm A')) - ciL;

    const openShift = () => {
        setShiftOpen(v => !v);
    }

    function handleApply() {
        setSaved(true);
        setTimeout(() => setSaved(false), 1800);
    }

    const declineOT = async (itemId) => {
        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/ot-declined/' + itemId, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const approveOT = async (itemId) => {
        setLoading(true);
        setIsModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/time-tracking/ot-approved/' + itemId, { start: dayjs(shiftStart, 'HH:mm').format('HH:mm:ss'), end: dayjs(shiftEnd, 'HH:mm').format('HH:mm:ss') }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            updateTimeTracking(response.data.data);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<div className={`${styles.modalOverlay}`} onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
        <div className={`${styles.modal}`}>

            <div className={`${styles.modalHead}`}>
                <div className={`${styles.mhLeft}`}>
                    <div className={`${styles.av} ${styles.avPl}`} style={{ width: 34, height: 34, fontSize: 10, flexShrink: 0 }}>
                        {selectedEmp?.user_image_url ? <img src={selectedEmp?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : selectedEmp?.initial}
                    </div>
                    <div>
                        <div className={`${styles.modalName}`}>{selectedEmp.name}</div>
                        <div className={`${styles.modalDate}`}>{dayjs(selectedDay?.date).format('ddd D, MMM YYYY')}</div>
                    </div>
                </div>
                <button className={`${styles.modalClose}`} onClick={(e) => setIsModalOpen(false)} aria-label="Close">✕</button>
            </div>

            <div className={`${styles.timelineWrap}`}>
                <div className={`${styles.tlLabel}`}>Shift timeline</div>
                <div className={`${styles.tlBarWrap}`}>
                    <div className={`${styles.tlTrack}`} />
                    <div className={`${styles.tlSched}`} style={{ left: `${schedL}%`, width: `${schedW}%` }} />
                    <div className={`${styles.tlWorked}`} style={{ left: `${ciL}%`, width: `${workedW}%` }} />
                </div>
                <div className={`${styles.tlTimes}`}>
                    <span>8 AM</span><span>3 PM</span><span>10 PM</span>
                </div>
                <div className={`${styles.tlCiLbl}`}>
                    <span className={`${styles.tlBadge} ${styles.tlBadgeIn}`}>{dayjs(selectedDay?.checkin_time, 'HH:mm:ss').format('hh:mm A')}</span>
                    <span className={`${styles.tlArrow}`}>→</span>
                    {selectedDay?.checkout_time ? <span className={`${styles.tlBadge} ${styles.tlBadgeOut}`}>{dayjs(selectedDay?.checkout_time, 'HH:mm:ss').format('hh:mm')}</span> : dayjs(selectedDay?.date).isSame(dayjs(), "day") ? <span className={`${styles.tlBadge} ${styles.tlBadgeProg}`}>Now</span> : <span className={`${styles.tlBadge} ${styles.tlBadgeMiss}`}>No out</span>}
                    <span className={`${styles.tlDur}`}>{formatMinutesToHM(selectedDay?.working_time)} Worked</span>
                </div>
            </div>

            <div className={`${styles.modalBody}`}>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>⏱ Scheduled shift</span>
                    <span className={`${styles.irVal}`}>{selectedEmp?.checkin_time + " - " + selectedEmp?.checkout_time}</span>
                </div>
                <div className={`${styles.infoRow}`}>
                    <span className={`${styles.irLbl}`}>◎ Shift target</span>
                    <span className={`${styles.irVal}`}>{formatMinutesToHM(selectedDay?.shift_minutes)}</span>
                </div>
                <div className={`${styles.infoRow} ${styles.infoRowLast}`}>
                    <span className={`${styles.irLbl}`}>⌛ Total worked</span>
                    <span className={selectedDay?.is_ot_approved > 0 ? `${styles.irVal} ${styles.irOt}` : selectedDay?.late ? `${styles.irVal} ${styles.irLate}` : `${styles.irVal} ${styles.irOk}`}>{formatMinutesToHM(selectedDay?.regular_minutes + selectedDay?.ot_time)}</span>
                </div>
            </div>

            {selectedDay?.is_ot_approved === 1 && (<>
                <div className={`${styles.otBox}`}>
                    <div className={`${styles.otBoxTitle}`}>⚠ Overtime detected</div>
                    <div className={`${styles.otAmount}`}>+{formatMinutesToHM(selectedDay?.ot_time)} overtime</div>
                    <div className={`${styles.otDesc}`}>
                        Worked {formatMinutesToHM(selectedDay?.ot_time)} beyond the {formatMinutesToHM(selectedDay?.shift_minutes)} scheduled shift.
                        Approve to confirm paid overtime, or decline to mark as unpaid.
                    </div>
                </div>

                <div className={`${styles.shiftSection}`}>
                    <button className={`${styles.shiftToggleBtn}`} onClick={openShift}>
                        ✏ {shiftOpen ? "Hide shift editor" : "Adjust shift hours (optional)"}
                    </button>
                    {shiftOpen && (
                        <div className={`${styles.shiftEditBody}`}>
                            <div className={`${styles.seLbl}`}>Override shift hours</div>
                            <div className={`${styles.shiftInputs}`}>
                                <input className={`${styles.shiftInput}`} type="time" value={shiftStart} onChange={e => setShiftStart(e.target.value)} aria-label="Shift start" />
                                <span className={`${styles.shiftSep}`}>–</span>
                                <input className={`${styles.shiftInput}`} type="time" value={shiftEnd} onChange={e => setShiftEnd(e.target.value)} aria-label="Shift end" />
                                <button className={`${styles.applyBtn} ${saved ? styles.applyBtnSaved : ""}`} onClick={handleApply}>
                                    {saved ? "Saved ✓" : "Apply"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </>)}

            <div className={`${styles.modalFooter}`}>
                {selectedDay?.is_ot_approved > 1 && <>
                    <span className={`${styles.footerStatus} ${selectedDay?.is_ot_approved === 2 ? styles.footerOk : styles.footerNo}`}>
                        {selectedDay?.is_ot_approved === 2 && "✓ OT Approved"}
                        {selectedDay?.is_ot_approved === 3 && "✕ OT Declined"}
                    </span>
                    <button className={`${styles.btnSecondary}`} onClick={(e) => setIsModalOpen(false)}>Close</button>
                </>}
                {selectedDay?.is_ot_approved === 1 && <>
                    <button className={`${styles.btnDanger}`} onClick={(e) => declineOT(selectedDay?.id)}>Decline OT</button>
                    <button className={`${styles.btnPrimary}`} onClick={(e) => approveOT(selectedDay?.id)}>✓ Approve OT</button>
                </>}
                {selectedDay?.is_ot_approved === 0 && <button className={`${styles.btnSecondary}`} onClick={(e) => setIsModalOpen(false)}>Close</button>}
            </div>

        </div>
    </div >
    );
}

const DayCell = ({ emplyeeItem, item, classN, workingDaysArr, setIsModalOpen, setSelectedEmp, setSelectedDay }) => {

    if (!workingDaysArr.includes(item?.day)) {
        return <span className={`${styles.offLbl}`}>off</span>;
    }

    if (!item?.checkin_time) {
        return <span className={`${styles.offLbl}`}>off</span>;
    }

    const btnCls = [styles.dayBtn, item?.is_ot_approved === 2 ? styles.resolvedOk : item?.is_ot_approved === 3 ? styles.resolvedNo : item?.is_ot_approved === 1 ? styles.hasOt :
        item?.late ? styles.isLate : ""].join(" ");

    const outCls = item?.checkout_time ? styles.dtOut : dayjs(item?.date).isSame(dayjs(), "day") ? [styles.dtOut, styles.dtOutProg].join(" ") : [styles.dtOut, styles.dtOutMiss].join(" ");

    const dhCls = [styles.dh, item?.is_ot_approved > 0 ? styles.dhOt : item?.late ? styles.dhLate : ""].join(" ");

    const chip = item?.is_ot_approved === 2 ? <span className={`${styles.cellTag} ${styles.tagApproved}`}>✓ OT approved</span> : item?.is_ot_approved === 3 ? <span className={`${styles.cellTag} ${styles.tagDeclined}`}>OT declined</span> : item?.is_ot_approved === 1 ? <span className={`${styles.cellTag} ${styles.tagOt}`}>+{item?.is_ot_approved === 2 ? item?.approved_ot_time : formatMinutesToHM(item?.ot_time)}</span> : item?.late ? <span className={`${styles.cellTag} ${styles.tagLate}`}>late -{item?.late}</span> : null;

    return (<button className={btnCls} onClick={(e) => { setIsModalOpen(true); setSelectedEmp(emplyeeItem); setSelectedDay(item); }}>
        <div className={`${styles.cellTopRow}`}>
            <span className={dhCls}>{formatMinutesToHM(item?.working_time)}</span>
            {chip}
        </div>
        <div className={`${styles.dtRow}`}>
            <span className={`${styles.dtIn}`}>{item?.checkin_time ? dayjs(item?.checkin_time, 'HH:mm:ss').format('hh:mm') : '-'}</span>
            <span className={`${styles.dtSep}`}>→</span>
            <span className={outCls}>{item?.checkout_time ? dayjs(item?.checkout_time, 'HH:mm:ss').format('hh:mm') : dayjs(item?.date).isSame(dayjs(), "day") ? 'now' : '!out'}</span>
        </div>
    </button>);
}

const WeekViewTable = ({ contentLoading, filteredData, dateRange, setLoading, updateTimeTracking }) => {
    const [dayLabels, setDayLabels] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);

    useEffect(() => {
        const startDate = dayjs(dateRange?.start);
        const endDate = dayjs(dateRange?.end);

        const dates = [];

        let current = startDate;

        while (current.isBefore(endDate) || current.isSame(endDate, "day")) {
            dates.push(current.format("YYYY-MM-DD"));
            current = current.add(1, "day");
        }

        setDayLabels(dates);
    }, [dateRange]);

    return (<div className={`${styles.tblWrap}`}>
        {contentLoading && <div className="Loader TableLoader"><TableLoaderSmall /></div>}

        {!contentLoading && filteredData?.length > 0 && <table className={`${styles.table}`}>
            <thead>
                <tr>
                    <th className={`${styles.thEc}`}>Employee</th>
                    {dayLabels.map((d, i) => (
                        <th key={d} className={d === dayjs().format('YYYY-MM-DD') ? `${styles.thTc}` : `${styles.th}`}>{dayjs(d).format('MMM D')}</th>
                    ))}
                    <th className={`${styles.thTot}`}>Regular Hrs</th>
                    <th className={`${styles.thTot}`}>OT Hrs</th>
                    <th className={`${styles.thTot}`}>Wage</th>
                </tr>
            </thead>
            <tbody>
                {filteredData.map((item, index) => {
                    let classN = `${styles.av}`;

                    if (index % 5 === 0) {
                        classN = `${styles.av} ${styles.avPl}`;
                    }
                    if (index % 5 === 1) {
                        classN = `${styles.av} ${styles.avTw}`;
                    }
                    if (index % 5 === 2) {
                        classN = `${styles.av} ${styles.avCh}`;
                    }
                    if (index % 5 === 3) {
                        classN = `${styles.av} ${styles.avMj}`;
                    }
                    if (index % 5 === 4) {
                        classN = `${styles.av} ${styles.avRl}`;
                    }

                    let regularMinutes = 0;
                    let OTMinutes = 0;
                    let wageTotal = 0;

                    item?.timeTracking?.forEach(i => {
                        regularMinutes += i?.regular_minutes;
                        OTMinutes += i?.approved_ot_time;

                        if (item?.wage > 0 && i?.regular_minutes > 0) {
                            wageTotal += (i?.regular_minutes / 60) * item?.wage;
                        }

                        if (item?.ot_wage > 0 && i?.approved_ot_time > 0) {
                            wageTotal += (i?.approved_ot_time / 60) * item?.ot_wage;
                        }
                    });

                    wageTotal = Math.round(wageTotal);

                    return <tr key={item?.id} className={`${styles.tbodyTr}`}>
                        <td className={`${styles.tdEc}`}>
                            <div className={`${styles.empWrap}`}>
                                <div className={`${classN}`}>{item?.user_image_url ? <img src={item?.user_image_url} alt="User Avatar" style={{ maxHeight: '28px', maxWidth: '28px' }} /> : item?.initial}</div>
                                <div>
                                    <div className={`${styles.empName}`}>{item?.name}</div>
                                    <div className={`${styles.empRole}`}>{item?.role}</div>
                                </div>
                            </div>
                        </td>
                        {item?.timeTracking.map((subItem, indd) => (<td key={subItem} className={dayjs(subItem?.date).isSame(dayjs(), 'day') ? `${styles.td} ${styles.todayBg}` : `${styles.td}`}>
                            <DayCell emplyeeItem={item} item={subItem} workingDaysArr={item?.working_days_arr} setIsModalOpen={setIsModalOpen.bind(this)} setSelectedEmp={setSelectedEmp.bind(this)} setSelectedDay={setSelectedDay.bind(this)} />
                        </td>))}
                        < td className={`${styles.td}`}>
                            <span className={`${styles.totalCell}`}>
                                {formatMinutesToHM(regularMinutes)}
                            </span>
                        </td>
                        < td className={`${styles.td}`}>
                            <span className={`${styles.totalCell}`}>
                                {formatMinutesToHM(OTMinutes)}
                            </span>
                        </td>
                        < td className={`${styles.td}`}>
                            <span className={`${styles.totalCell}`}>
                                {wageTotal ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(wageTotal) : '-'}
                            </span>
                        </td>
                    </tr>
                })}
            </tbody>
        </table >}

        {filteredData?.length === 0 && <>
            <div className={`${styles.hrBody}`}>
                <div className={`${styles.panel} ${styles.panelEmpty}`}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "#ccc", marginBottom: 10 }} aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                        <line x1="4" y1="4" x2="20" y2="20" />
                    </svg>
                    <div className={`${styles.emptyTitle}`}>Time tracking records unavailable.</div>
                    <div className={`${styles.emptySub}`}>Time entries will appear here once tracking starts.</div>
                </div>
            </div>
        </>}


        {isModalOpen && (<DayModal setIsModalOpen={setIsModalOpen.bind(this)} selectedEmp={selectedEmp} selectedDay={selectedDay} setLoading={setLoading.bind(this)} updateTimeTracking={updateTimeTracking.bind(this)} />)}
    </div >)

}

export default WeekViewTable;